import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Avatar,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
} from '@mui/material';
import {
  AccessTime,
  TrendingUp,
  Assignment,
  AttachMoney,
  People,
  PlayArrow,
  Stop,
  Visibility,
  MoreVert,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { aiTrackingService } from '../services/aiTrackingService';
import { useAuth } from '../contexts/AuthContext';

interface DashboardStats {
  todayHours: number;
  weekHours: number;
  monthHours: number;
  activeProjects: number;
  totalProjects: number;
  monthlyRevenue: number;
  teamMembers: number;
  completedTasks: number;
  pendingTasks: number;
}

interface RecentActivity {
  id: string;
  type: 'time_entry' | 'project_created' | 'expense_added' | 'invoice_sent';
  description: string;
  timestamp: string;
  user?: string;
  amount?: number;
  project?: string;
}

interface ActiveTimeEntry {
  id: string;
  projectName: string;
  description: string;
  startTime: string;
  duration: number;
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  trend?: { value: number; isPositive: boolean };
}> = ({ title, value, subtitle, icon, color, trend }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
    <CardContent sx={{ pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            backgroundColor: `${color}20`,
            color: color,
            mr: 2,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      {trend && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TrendingUp
            sx={{
              fontSize: 16,
              color: trend.isPositive ? 'success.main' : 'error.main',
              transform: trend.isPositive ? 'none' : 'rotate(180deg)',
              mr: 0.5,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: trend.isPositive ? 'success.main' : 'error.main',
              fontWeight: 'medium',
            }}
          >
            {trend.isPositive ? '+' : ''}{trend.value}% from last week
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

const QuickActionCard: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  onClick: () => void;
}> = ({ title, description, icon, color, onClick }) => (
  <Card 
    sx={{ 
      height: '100%', 
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 3,
      },
    }}
    onClick={onClick}
  >
    <CardContent sx={{ textAlign: 'center', py: 3 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: '50%',
          backgroundColor: `${color}20`,
          color: color,
          display: 'inline-flex',
          mb: 2,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </CardContent>
  </Card>
);

export const DashboardOverview: React.FC<{ onNavigate: (tab: string) => void }> = ({ onNavigate }) => {
  const { state: authState } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    todayHours: 0,
    weekHours: 0,
    monthHours: 0,
    activeProjects: 0,
    totalProjects: 0,
    monthlyRevenue: 0,
    teamMembers: 0,
    completedTasks: 0,
    pendingTasks: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [activeTimeEntry, setActiveTimeEntry] = useState<ActiveTimeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiTrackingStatus, setAiTrackingStatus] = useState(aiTrackingService.getStatus());

  useEffect(() => {
    loadDashboardData();
    
    // Update AI tracking status periodically
    const interval = setInterval(() => {
      setAiTrackingStatus(aiTrackingService.getStatus());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load dashboard stats
      try {
        const dashboardData = await apiService.getDashboardStats();
        setStats(dashboardData);
      } catch (error) {
        console.log('Using mock dashboard stats');
        // Use mock data if API fails
        setStats({
          todayHours: 6.5,
          weekHours: 32.5,
          monthHours: 142.5,
          activeProjects: 3,
          totalProjects: 8,
          monthlyRevenue: 12500,
          teamMembers: 5,
          completedTasks: 24,
          pendingTasks: 8,
        });
      }

      // Load recent activity with mock data
      setRecentActivity([
        {
          id: '1',
          type: 'time_entry',
          description: 'Worked on project dashboard',
          timestamp: new Date().toISOString(),
          user: 'You',
          project: 'Dashboard Project',
        },
        {
          id: '2',
          type: 'project_created',
          description: 'Created new project: Mobile App',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          user: 'You',
        },
        {
          id: '3',
          type: 'expense_added',
          description: 'Added office supplies expense',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          user: 'You',
          amount: 150,
        },
      ]);

      // Check for active time entry (mock data)
      setActiveTimeEntry(null);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${month} ${day}, ${hours}:${minutes}`;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'time_entry':
        return <AccessTime sx={{ fontSize: 20 }} />;
      case 'project_created':
        return <Assignment sx={{ fontSize: 20 }} />;
      case 'expense_added':
        return <AttachMoney sx={{ fontSize: 20 }} />;
      case 'invoice_sent':
        return <AttachMoney sx={{ fontSize: 20 }} />;
      default:
        return <AccessTime sx={{ fontSize: 20 }} />;
    }
  };

  const quickActions = [
    {
      title: 'Start Tracking',
      description: 'Begin tracking time for a project',
      icon: <PlayArrow sx={{ fontSize: 32 }} />,
      color: '#4caf50',
      onClick: () => onNavigate('time'),
    },
    {
      title: 'New Project',
      description: 'Create a new project',
      icon: <Assignment sx={{ fontSize: 32 }} />,
      color: '#2196f3',
      onClick: () => onNavigate('projects'),
    },
    {
      title: 'Add Expense',
      description: 'Record a new expense',
      icon: <AttachMoney sx={{ fontSize: 32 }} />,
      color: '#ff9800',
      onClick: () => onNavigate('expenses'),
    },
    {
      title: 'View Reports',
      description: 'Analyze your productivity',
      icon: <TrendingUp sx={{ fontSize: 32 }} />,
      color: '#9c27b0',
      onClick: () => onNavigate('reports'),
    },
  ];

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Typography>Loading dashboard...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Welcome back, {authState.user?.name}! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with your time tracking today.
        </Typography>
      </Box>

      {/* Active Time Entry Alert */}
      {activeTimeEntry && (
        <Paper
          sx={{
            p: 3,
            mb: 4,
            backgroundColor: 'success.light',
            color: 'success.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTime sx={{ mr: 2 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Currently tracking: {activeTimeEntry.projectName}
              </Typography>
              <Typography variant="body2">
                {activeTimeEntry.description} • {formatDuration(activeTimeEntry.duration)}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="error"
            startIcon={<Stop />}
            onClick={() => onNavigate('time')}
          >
            Stop Timer
          </Button>
        </Paper>
      )}

      {/* AI Tracking Status */}
      {aiTrackingStatus.isRunning && (
        <Paper
          sx={{
            p: 3,
            mb: 4,
            backgroundColor: 'info.light',
            color: 'info.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: 'success.main',
                mr: 2,
                animation: 'pulse 2s infinite',
              }}
            />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                AI Tracking Active
              </Typography>
              <Typography variant="body2">
                Taking screenshots every {aiTrackingStatus.config?.interval || 30} seconds
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => onNavigate('ai-analytics')}
            sx={{ color: 'inherit', borderColor: 'currentColor' }}
          >
            View Analytics
          </Button>
        </Paper>
      )}

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
        <StatCard
          title="Today's Hours"
          value={formatDuration(stats.todayHours * 3600)}
          icon={<AccessTime />}
          color="#4caf50"
          trend={{ value: 12, isPositive: true }}
        />
        <StatCard
          title="This Week"
          value={formatDuration(stats.weekHours * 3600)}
          icon={<TrendingUp />}
          color="#2196f3"
          trend={{ value: 8, isPositive: true }}
        />
        <StatCard
          title="Active Projects"
          value={stats.activeProjects}
          subtitle={`of ${stats.totalProjects} total`}
          icon={<Assignment />}
          color="#ff9800"
        />
        <StatCard
          title="Monthly Revenue"
          value={`$${stats.monthlyRevenue.toLocaleString()}`}
          icon={<AttachMoney />}
          color="#9c27b0"
          trend={{ value: 15, isPositive: true }}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        {/* Quick Actions */}
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
              {quickActions.map((action, index) => (
                <QuickActionCard key={index} {...action} />
              ))}
            </Box>
          </Paper>

          {/* Recent Activity */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Recent Activity
              </Typography>
              <Button size="small" onClick={() => onNavigate('entries')}>
                View All
              </Button>
            </Box>
            <List>
              {recentActivity.slice(0, 5).map((activity, index) => (
                <React.Fragment key={activity.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ backgroundColor: 'primary.light', color: 'primary.main' }}>
                        {getActivityIcon(activity.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={activity.description}
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(activity.timestamp)}
                          </Typography>
                          {activity.user && (
                            <Chip
                              label={activity.user}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {activity.amount && (
                            <Chip
                              label={`$${activity.amount}`}
                              size="small"
                              color="success"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                    />
                    <IconButton size="small">
                      <MoreVert />
                    </IconButton>
                  </ListItem>
                  {index < recentActivity.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>

        {/* Sidebar */}
        <Box>
          {/* Team Overview */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Team Overview
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <People sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2">
                {stats.teamMembers} team members
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Tasks Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(stats.completedTasks / (stats.completedTasks + stats.pendingTasks)) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {stats.completedTasks} completed, {stats.pendingTasks} pending
              </Typography>
            </Box>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => onNavigate('team')}
            >
              Manage Team
            </Button>
          </Paper>

          {/* This Month Summary */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              This Month
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total Hours
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {formatDuration(stats.monthHours * 3600)}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Revenue Generated
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                ${stats.monthlyRevenue.toLocaleString()}
              </Typography>
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={() => onNavigate('reports')}
            >
              View Detailed Reports
            </Button>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};
